/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ActivitySplits
 * --------------
 * Per-km splits of an activity. `useSplitRows` reads them once for both the
 * drawer list and the expanded view, adding where each split starts along the
 * route so a hovered row can be highlighted on the map.
 *
 * Mirrors the app's splits card (ActivityDetailFragment + SplitAdapter):
 *  - shown whenever the activity has at least one split, for any activity
 *    type. The app records one per full kilometre plus a final partial one of
 *    200 m or more, so a walk of 0.2–1 km has a single split and one under
 *    200 m has none;
 *  - header "Splits" with "AVG <pace> / km", the plain mean of the split paces;
 *  - each row: split number · a bar whose fill shrinks with pace (fastest =
 *    full, slowest = 45%, 50% when there is nothing to compare), the fastest
 *    in the accent and the rest in ink · the pace, or "—" if none was recorded;
 *  - the avg-pace explainer underneath.
 */

import React from 'react';
import { Skeleton } from './FeedParts';
import { useSplits } from '../../pages/feed/useFeedData';
import { formatPace } from '../../pages/feed/feedModel';

export function useSplitRows(activityId, enabled) {
  const { data, isLoading } = useSplits(activityId, enabled);
  let startM = 0;
  const rows = (data || [])
    .map((s) => ({
      n: Number(s.split_number),
      distM: Number(s.distance_meters) || 0,
      pace: Number(s.pace_seconds_per_km) || (Number(s.duration_seconds) / (Number(s.distance_meters) / 1000)),
    }))
    .sort((a, b) => a.n - b.n)
    .filter((s) => Number.isFinite(s.n))
    .map((s) => {
      const row = { ...s, pace: Number.isFinite(s.pace) && s.pace > 0 ? s.pace : null, startM };
      startM += s.distM || 1000;
      return row;
    });
  const paces = rows.map((s) => s.pace).filter((p) => p !== null);
  const best = paces.length ? Math.min(...paces) : null;
  const worst = paces.length ? Math.max(...paces) : null;
  // The app averages the split paces as whole seconds (integer division).
  const avg = paces.length ? Math.floor(paces.reduce((a, p) => a + Math.round(p), 0) / paces.length) : null;
  return { rows, best, worst, avg, isLoading };
}

export const AVG_PACE_NOTE = 'Avg Pace is how many minutes it took you to cover each kilometer, on average — the lower the number, the faster you were moving.';

/** The card header, as the app writes it: "Splits" and "AVG 5'12\" / km". */
export function SplitsHeader({ avg }) {
  return (
    <div className="fd-d-sec-h"><span className="fd-eyebrow">Splits</span>{avg !== null && <span>AVG {formatPace(avg)} / km</span>}</div>
  );
}

/**
 * Bar fill for a split, the app's formula: fastest = full, slowest = 45%,
 * clamped to 5–100%; 50% when the split has no pace or there is no spread
 * (a single split, or all equal).
 */
export function splitBarWidth(pace, best, worst) {
  if (pace === null || best === null || worst === null || !(worst > best)) return '50%';
  const ratio = 1 - (0.55 * (pace - best)) / (worst - best);
  return `${Math.max(0.05, Math.min(1, ratio)) * 100}%`;
}

export default function Splits({ activityId, enabled }) {
  const { rows, best, worst, avg, isLoading } = useSplitRows(activityId, enabled);
  if (isLoading) return <div className="fd-d-sec"><Skeleton height={120} /></div>;
  if (!rows.length) return null;
  return (
    <div className="fd-d-sec">
      <SplitsHeader avg={avg} />
      <div className="fd-splits">
        {rows.slice(0, 50).map((s) => (
          <div key={s.n} className={`fd-split${s.pace !== null && s.pace === best ? ' best' : ''}`}>
            <span>{s.n}</span>
            <span className="tr"><i style={{ width: splitBarWidth(s.pace, best, worst) }} /></span>
            <span style={{ textAlign: 'right' }}>{formatPace(s.pace)}</span>
          </div>
        ))}
      </div>
      <p className="fd-split-note">{AVG_PACE_NOTE}</p>
    </div>
  );
}
